import React, { Suspense, lazy } from 'react';
import { CircularProgress } from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { StudentProvider } from './contexts/StudentContext';
import { TeacherProvider } from './contexts/TeacherContext';
import { CourseProvider } from './contexts/CourseContext';
import { GradeProvider } from './contexts/GradeContext';
import { AdminProvider } from './contexts/AdminContext';
import { ParentProvider } from './contexts/ParentContext';
import { AttendanceProvider } from './contexts/AttendanceContext';
import { EnrollmentProvider } from './contexts/EnrollmentContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { GradeMarkProvider } from './contexts/GradeMarkContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SectionProvider } from './contexts/SectionContext';
import { TeacherGradeProvider } from './contexts/TeacherGradeContext';
import { TeacherStudentProvider } from './contexts/TeacherStudentContext';
import { TimetableProvider } from './contexts/TimetableContext';
import { AcademicYearProvider } from './contexts/AcademicYearContext';
import { GradeLevelProvider } from './contexts/GradelevelContext';
import { UsersProvider } from './contexts/UsersContext';
import { AnnouncementProvider } from './contexts/AnnouncementContext';
import { ResignationProvider } from './contexts/ResignationContext';
import { StudentHistoryProvider } from './contexts/StudentHistoryContext';
import { TeacherHistoryProvider } from './contexts/TeacherHistoryContext';
import { FeeProvider } from './contexts/FeeContext';
import { AssessmentProvider } from './contexts/AssessmentContext';
import { StudentCardProvider } from './contexts/StudentCardContext';
import { StaffProvider } from './contexts/StaffContext';
import { AllUsersProvider } from './contexts/AllUsersContext';
import { TermProvider } from './contexts/TermContext';
import { GradingPolicyProvider } from './contexts/GradingPolicyContext';
import { GradingScaleProvider } from './contexts/GradingScaleContext';
import { StaffHistoryProvider } from './contexts/StaffHistoryContext';
// library
import { DigitalResourceProvider } from './contexts/DigitalResourceContext';
import { LibraryBookProvider } from './contexts/LibraryBookContext';
import { BorrowRecordProvider } from './contexts/BorrowRecordContext';
import { LateBookProvider } from './contexts/LateBookContext';
import { ReservationProvider } from './contexts/ReservationContext';


import DashboardLayout from './components/Common/DashboardLayout';
import PrivateRoute from './routes/PrivateRoute';
import Footer from './components/Common/Footer';
import ErrorHandler from './errorpages/ErrorHandler';


import './assets/styles/index.css';
// Lazy-loaded components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminRoutes = lazy(() => import('./routes/AdminRoutes'));
const TeacherRoutes = lazy(() => import('./routes/TeacherRoutes'));
const StaffRoutes = lazy(() => import('./routes/StaffRoutes'));
const StudentRoutes = lazy(() => import('./routes/StudentRoutes'));
const ParentRoutes = lazy(() => import('./routes/ParentRoutes'));
const PublicRoutes = lazy(() => import('./routes/PublicRoutes'));

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div className="loading-spinner">Something went wrong.</div>;
    }

    return this.props.children;
  }
}

const App = () => {
  return (
    <AuthProvider>
      <StudentProvider>
        <TeacherProvider>
          <CourseProvider>
            <GradeProvider>
              <AdminProvider>
                <ParentProvider>
                  <AttendanceProvider>
                    <EnrollmentProvider>
                      <FeedbackProvider>
                        <GradeMarkProvider>
                          <NotificationProvider>
                            <SectionProvider>
                              <TeacherGradeProvider>
                                <TeacherStudentProvider>
                                  <TimetableProvider>
                                    <AcademicYearProvider>
                                      <GradeLevelProvider>
                                        <UsersProvider>
                                          <AnnouncementProvider>
                                            <ResignationProvider>
                                              <StudentHistoryProvider>
                                                <TeacherHistoryProvider>
                                                  <TermProvider>
                                                    <FeeProvider>
                                                      <AssessmentProvider>
                                                        <StudentCardProvider>
                                                          <DigitalResourceProvider>
                                                            <LibraryBookProvider>
                                                              <BorrowRecordProvider>
                                                                <LateBookProvider>
                                                                  <ReservationProvider>
                                                                    <StaffProvider>
                                                                      <AllUsersProvider>
                                                                        <GradingPolicyProvider>
                                                                          <GradingScaleProvider>
                                                                            <StaffHistoryProvider>
                                                                              <DashboardLayout />
                                                                              <div className="content-container">
                                                                                <ErrorBoundary>
                                                                                  <Suspense fallback={<div className="loading-spinner"> <CircularProgress /></div>}>
                                                                                    <Routes>
                                                                                      <Route path="/login" element={<LoginPage />} />
                                                                                      <Route path="/admin/*" element={<PrivateRoute element={AdminRoutes} roles={['admin', 'super-admin']} />} />
                                                                                      <Route path="/teacher/*" element={<PrivateRoute element={TeacherRoutes} roles={['admin', 'super-admin', 'teacher']} />} />
                                                                                      <Route path="/staff/*" element={<PrivateRoute element={StaffRoutes} roles={['admin', 'super-admin', 'staff']} />} />
                                                                                      <Route path="/student/*" element={<PrivateRoute element={StudentRoutes} roles={['admin', 'super-admin', 'student', 'parent']} />} />
                                                                                      <Route path="/parent/*" element={<PrivateRoute element={ParentRoutes} roles={['admin', 'super-admin', 'student', 'parent', 'guardian']} />} />
                                                                                      <Route path="/*" element={<PublicRoutes />} />
                                                                                      <Route path="/error/:errorCode" element={<ErrorHandler />} />

                                                                                      <Route path="*" element={<Navigate to="/error/404" />} />
                                                                                    </Routes>
                                                                                  </Suspense>
                                                                                </ErrorBoundary>
                                                                              </div>
                                                                              <Footer />
                                                                            </StaffHistoryProvider>
                                                                          </GradingScaleProvider>
                                                                        </GradingPolicyProvider>
                                                                      </AllUsersProvider>
                                                                    </StaffProvider>
                                                                  </ReservationProvider>
                                                                </LateBookProvider>
                                                              </BorrowRecordProvider>
                                                            </LibraryBookProvider>
                                                          </DigitalResourceProvider>
                                                        </StudentCardProvider>
                                                      </AssessmentProvider>
                                                    </FeeProvider>
                                                  </TermProvider>
                                                </TeacherHistoryProvider>
                                              </StudentHistoryProvider>
                                            </ResignationProvider>
                                          </AnnouncementProvider>
                                        </UsersProvider>
                                      </GradeLevelProvider>
                                    </AcademicYearProvider>
                                  </TimetableProvider>
                                </TeacherStudentProvider>
                              </TeacherGradeProvider>
                            </SectionProvider>
                          </NotificationProvider>
                        </GradeMarkProvider>
                      </FeedbackProvider>
                    </EnrollmentProvider>
                  </AttendanceProvider>
                </ParentProvider>
              </AdminProvider>
            </GradeProvider>
          </CourseProvider>
        </TeacherProvider>
      </StudentProvider>
    </AuthProvider>
  );
};

export default App;
